/**
 * JourneysOverviewScreen
 * The home page - overview of all journeys showing their main goals
 * with ability to check off goals and navigate to details
 */

import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
} from 'react-native';
import { useGoals } from '../hooks/useGoals';
import { useCompletions } from '../hooks/useCompletions';
import { getTodayDateString } from '../utils/dateUtils';
import { Goal } from '../models/Goal';

type JourneysOverviewScreenProps = {
  navigation: any;
};

export const JourneysOverviewScreen: React.FC<JourneysOverviewScreenProps> = ({ navigation }) => {
  const { accounts, goals, switchAccount } = useGoals();
  const { isCompletedToday, toggleToday, getDaysSinceLastCompletion } = useCompletions();

  const today = getTodayDateString();

  // Get main goal and daily actions for each journey with stable random selection
  const journeysWithGoals = useMemo(() => {
    return accounts.map(account => {
      const mainGoal = goals.find(g => g.accountId === account.id && g.type === 'main');
      const sideGoals = goals.filter(g => g.accountId === account.id && g.type === 'side');
      // Pick a stable random index based on today's date and account id
      const dateHash = today.split('-').reduce((a, b) => a + parseInt(b, 10), 0);
      const accountHash = account.id.charCodeAt(0) + account.id.charCodeAt(account.id.length - 1);
      const randomIndex = sideGoals.length > 0 ? (dateHash + accountHash) % sideGoals.length : 0;
      return {
        account,
        mainGoal,
        sideGoals,
        sideGoalsCount: sideGoals.length,
        randomSideGoal: sideGoals[randomIndex] || null,
      };
    });
  }, [accounts, goals, today]);

  const handleToggleGoal = async (accountId: string, goalId: string) => {
    await toggleToday(accountId, goalId);
  };

  const handleGoToJourney = async (accountId: string) => {
    await switchAccount(accountId);
    navigation.navigate('Today');
  };

  // Get a daily action to display - either one completed today or the random suggestion
  const getDailyActionDisplay = (
    sideGoals: Goal[],
    randomSideGoal: Goal | null
  ): { text: string; isCompleted: boolean } | null => {
    if (sideGoals.length === 0) return null;

    // First, check if any daily action was completed today
    const completedToday = sideGoals.find(g => isCompletedToday(g.id));
    if (completedToday) {
      return { text: completedToday.name, isCompleted: true };
    }

    // Otherwise, show the pre-selected random daily action as a suggestion
    if (randomSideGoal) {
      return { text: randomSideGoal.name, isCompleted: false };
    }

    return null;
  };

  // Format days since last check
  const formatDaysSince = (mainGoalId: string): string | null => {
    const daysSince = getDaysSinceLastCompletion(mainGoalId);
    if (daysSince === 0) {
      // Check if completed today
      if (isCompletedToday(mainGoalId)) {
        return null; // Don't show anything if completed today
      }
      return 'Not yet checked';
    }
    if (daysSince === 1) return '1 day ago';
    return `${daysSince} days ago`;
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>All Journeys</Text>
        <Text style={styles.headerSubtitle}>
          {journeysWithGoals.length} {journeysWithGoals.length === 1 ? 'journey' : 'journeys'} • {today}
        </Text>
      </View>

      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        {journeysWithGoals.map(({ account, mainGoal, sideGoals, sideGoalsCount, randomSideGoal }) => {
          const isCompleted = mainGoal ? isCompletedToday(mainGoal.id) : false;
          const themeColor = account.themeColor || '#007AFF';
          const dailyAction = mainGoal ? getDailyActionDisplay(sideGoals, randomSideGoal) : null;
          const daysSince = mainGoal ? formatDaysSince(mainGoal.id) : null;

          return (
            <TouchableOpacity
              key={account.id}
              style={[
                styles.journeyCard,
                { borderLeftColor: themeColor },
                isCompleted && styles.journeyCardCompleted,
              ]}
              onPress={() => mainGoal && handleGoToJourney(account.id)}
              activeOpacity={mainGoal ? 0.7 : 1}
            >
              {/* Journey Header */}
              <View style={styles.journeyHeader}>
                <View style={styles.journeyInfo}>
                  <View style={styles.journeyNameRow}>
                    <Text style={styles.journeyName}>{account.name}</Text>
                    {daysSince && (
                      <Text style={styles.daysSinceText}>{daysSince}</Text>
                    )}
                  </View>
                  {mainGoal && (
                    <Text style={[styles.mainGoalText, { color: themeColor }]}>
                      {mainGoal.name}
                    </Text>
                  )}
                </View>

                {/* Checkbox */}
                {mainGoal && (
                  <TouchableOpacity
                    style={[
                      styles.checkbox,
                      isCompleted && { backgroundColor: themeColor, borderColor: themeColor },
                    ]}
                    onPress={(e) => {
                      e.stopPropagation();
                      handleToggleGoal(account.id, mainGoal.id);
                    }}
                  >
                    {isCompleted && <Text style={styles.checkmark}>✓</Text>}
                  </TouchableOpacity>
                )}
              </View>

              {/* Daily Action Display */}
              {dailyAction && (
                <View style={styles.dailyActionContainer}>
                  <Text style={styles.dailyActionLabel}>
                    {dailyAction.isCompleted ? 'Done today:' : 'Try today:'}
                  </Text>
                  <Text
                    style={[
                      styles.dailyActionText,
                      dailyAction.isCompleted && styles.dailyActionCompleted,
                    ]}
                    numberOfLines={1}
                  >
                    {dailyAction.text}
                  </Text>
                </View>
              )}

              {/* Footer with daily actions count */}
              {mainGoal && (
                <View style={styles.journeyFooter}>
                  <Text style={[styles.footerText, { color: themeColor }]}>
                    {sideGoalsCount > 0
                      ? `${sideGoalsCount} daily ${sideGoalsCount === 1 ? 'action' : 'actions'}`
                      : 'Tap to add daily actions'}
                  </Text>
                  <Text style={[styles.footerArrow, { color: themeColor }]}>→</Text>
                </View>
              )}

              {/* No main goal state */}
              {!mainGoal && (
                <TouchableOpacity
                  style={styles.createGoalButton}
                  onPress={async () => {
                    await switchAccount(account.id);
                    navigation.navigate('CreateMainGoal', { accountId: account.id });
                  }}
                >
                  <Text style={[styles.createGoalText, { color: themeColor }]}>
                    + Create Main Goal
                  </Text>
                </TouchableOpacity>
              )}
            </TouchableOpacity>
          );
        })}

        {/* Create New Journey */}
        <TouchableOpacity
          style={styles.createJourneyButton}
          onPress={() => navigation.navigate('CreateAccount')}
        >
          <Text style={styles.createJourneyText}>+ Create New Journey</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    backgroundColor: '#fff',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#333',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  journeyCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderLeftWidth: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  journeyCardCompleted: {
    backgroundColor: '#f0f9ff',
  },
  journeyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  journeyInfo: {
    flex: 1,
    marginRight: 12,
  },
  journeyNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  journeyName: {
    fontSize: 12,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  daysSinceText: {
    fontSize: 11,
    color: '#999',
    marginLeft: 8,
    fontStyle: 'italic',
  },
  mainGoalText: {
    fontSize: 20,
    fontWeight: '600',
    color: '#007AFF',
  },
  checkbox: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: '#ddd',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkmark: {
    fontSize: 20,
    color: '#fff',
    fontWeight: '700',
  },
  dailyActionContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  dailyActionLabel: {
    fontSize: 11,
    color: '#999',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  dailyActionText: {
    fontSize: 15,
    color: '#555',
  },
  dailyActionCompleted: {
    color: '#4CAF50',
    fontWeight: '500',
  },
  journeyFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  footerText: {
    fontSize: 13,
    fontWeight: '500',
  },
  footerArrow: {
    fontSize: 16,
    fontWeight: '600',
  },
  createGoalButton: {
    paddingVertical: 8,
  },
  createGoalText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#007AFF',
  },
  createJourneyButton: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#007AFF',
    borderStyle: 'dashed',
  },
  createJourneyText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
  },
});
